import { Extension } from '@tiptap/core';
import { Node as ProsemirrorNode } from 'prosemirror-model';
import { Plugin, PluginKey, EditorState, Transaction } from 'prosemirror-state';

// Helper function to check node type
function nodeEqualsType({ node, types }: { types: any | any[]; node: ProsemirrorNode | null | undefined }): boolean {
  if (!node) {
    return false;
  }

  return Array.isArray(types) ? types.includes(node.type) : node.type === types;
}

// Helper function to check if a node has content
function nodeHasContent(node: ProsemirrorNode | null | undefined): boolean {
  if (!node) {
    return false;
  }

  // Check if the node has text content
  if (node.textContent && node.textContent.trim() !== '') {
    return true;
  }

  // Check if the node has child nodes (like images, etc.)
  if (node.childCount > 0) {
    // For paragraph nodes, check if any child has meaningful content
    for (let i = 0; i < node.childCount; i++) {
      const child = node.child(i);
      if (child.isText && child.textContent.trim() !== '') {
        return true;
      }
      // Non-text nodes (like images, etc.) count as content
      if (!child.isText) {
        return true;
      }
    }
  }

  return false;
}

// Helper function to check if the document contains any diff marks
function documentHasDiffMarks(doc: ProsemirrorNode): boolean {
  let hasDiffMarks = false;
  doc.descendants((node) => {
    if (node.marks && node.marks.some((mark: any) => mark.type.name === 'insertion' || mark.type.name === 'deletion')) {
      hasDiffMarks = true;
      return false; // Stop traversal
    }
  });
  return hasDiffMarks;
}

// Helper function to check if a node has content, ignoring diff marks
function nodeHasContentIgnoringDiffMarks(node: ProsemirrorNode | null | undefined): boolean {
  if (!node) {
    return false;
  }

  // Check if the node has text content
  if (node.textContent && node.textContent.trim() !== '') {
    // Check if all text content is marked for deletion (should not count as content)
    let hasNonDeletedContent = false;

    if (node.childCount > 0) {
      for (let i = 0; i < node.childCount; i++) {
        const child = node.child(i);
        if (child.isText) {
          const hasDeletionMark = child.marks.some((mark: any) => mark.type.name === 'deletion');
          if (child.textContent.trim() !== '' && !hasDeletionMark) {
            hasNonDeletedContent = true;
            break;
          }
        } else {
          // Non-text nodes count as content regardless of marks
          hasNonDeletedContent = true;
          break;
        }
      }
    } else {
      // Direct text content without child structure
      hasNonDeletedContent = true;
    }

    return hasNonDeletedContent;
  }

  // Check if the node has child nodes (like images, etc.)
  if (node.childCount > 0) {
    // For paragraph nodes, check if any child has meaningful content
    for (let i = 0; i < node.childCount; i++) {
      const child = node.child(i);
      if (child.isText) {
        const hasDeletionMark = child.marks.some((mark: any) => mark.type.name === 'deletion');
        if (child.textContent.trim() !== '' && !hasDeletionMark) {
          return true;
        }
      } else {
        // Non-text nodes (like images, etc.) count as content
        return true;
      }
    }
  }

  return false;
}

/**
 * Extension based on:
 * - https://github.com/ueberdosis/tiptap/blob/v1/packages/tiptap-extensions/src/extensions/TrailingNode.js
 * - https://github.com/remirror/remirror/blob/e0f1bec4a1e8073ce8f5500d62193e52321155b9/packages/prosemirror-trailing-node/src/trailing-node-plugin.ts
 */

export interface TrailingNodeOptions {
  /** The node type name to be added. */
  node: string;
  /** Node types where trailing node should not be added after. */
  notAfter: string[];
}

export const TrailingNode = Extension.create<TrailingNodeOptions>({
  name: 'trailingNode',

  addOptions() {
    return {
      node: 'paragraph',
      notAfter: [],
    };
  },

  addProseMirrorPlugins() {
    const plugin = new PluginKey(this.name);
    const options = this.options;
    const disabledNodes = Object.entries(this.editor.schema.nodes)
      .map(([, value]) => value)
      .filter((node) => this.options.notAfter.includes(node.name));

    return [
      new Plugin({
        key: plugin,
        // This function runs after each transaction and allows appending a new transaction
        // if necessary to add the trailing node.
        appendTransaction: (_, oldState: EditorState, newState: EditorState) => {
          const { doc, tr, schema } = newState;

          const shouldInsertNodeAtEnd = plugin.getState(newState);
          if (!shouldInsertNodeAtEnd) {
            return null;
          }

          const insertPosition = doc.content.size;
          const nodeType = schema.nodes[options.node];

          // Create and insert the specified node type at the end of the document.
          return tr.insert(insertPosition, nodeType.create());
        },
        state: {
          init: (_, state: EditorState) => {
            // Don't add trailing nodes if document contains diff marks
            if (documentHasDiffMarks(state.doc)) {
              return false;
            }

            const lastNode = state.doc.lastChild;
            // Return true if a trailing node should be added initially.
            // Only add if the last node is not disabled AND has content (ignoring diff marks)
            return (
              !nodeEqualsType({ node: lastNode, types: disabledNodes }) && nodeHasContentIgnoringDiffMarks(lastNode)
            );
          },
          apply: (tr: Transaction, value: boolean, oldState: EditorState, newState: EditorState) => {
            if (!tr.docChanged) {
              return value;
            }

            // Don't add trailing nodes if document contains diff marks
            if (documentHasDiffMarks(newState.doc)) {
              return false;
            }

            const lastNode = (tr.doc.lastChild?.content as any)?.content?.[0];

            // Return true if a trailing node should be added after this transaction.
            // Only add if the last node is not disabled AND has content (ignoring diff marks)
            return (
              !nodeEqualsType({ node: lastNode, types: disabledNodes }) && nodeHasContentIgnoringDiffMarks(lastNode)
            );
          },
        },
      }),
    ];
  },
});
